import { useState, useContext, useEffect, useRef, useMemo } from 'react'
import { CountMetricCard, Spinner } from '../common/atoms'
import { handleAppError } from '../handlers'
import { UserAlert } from '../common/atoms/alerts'
import { UndoModal, UploadModal, UploadStatusModal } from '../modals'
import { Table, Pagination, Tabbar } from '../common/molecules'
import { EmployeeDataHoc } from '../../utils/hocs'
import { AppDispatchContext, AppStateContext } from '../../utils/contexts'
import { manageEmployeesErrorTableSchema as tableSchema } from '../../schemas/table'
import { CorrectTick,  close_light_red, exclametry_red_dark } from '../../assets/images'
import { employeeDataApi, StepathonApi } from '../../services'
import moment from 'moment'
import {
  EMPLOYEE_PAGE_CONTENT,
  UPLOAD_QUERY_STATUS,
  CANCEL_QUERY_STATUS,
  DISABLE_ICON,
  PRE_EMP_BULK_CHECK,
  PPMC_BULK_CHECK
} from '../../constants/employeeData'
import { SALES_ROLE } from '../../constants/overview'
import { dsButtonPrimary, dsButtonSecondary, joinButtonClasses } from '../../utils/buttonStyles';

const ViewErrors = ({ history, location }) => {
  const { employee_data : {tabledata, file, csvData} } = useContext(AppStateContext)
  const dispatch = useContext(AppDispatchContext)

  const query = new URLSearchParams(location.search).get('of')
  const { [query]: CONTENT } = EMPLOYEE_PAGE_CONTENT
  const { data = [], headers = [], errorcount = 0 } = tabledata || {}
  const inValidData = useMemo(() => data.filter((item) => !!item.errors), [])
  const validDataCount = data.length - errorcount
  const backpath = [PRE_EMP_BULK_CHECK, PPMC_BULK_CHECK].includes(CONTENT['title']) ? CONTENT['breadcrumb']['r_link'] : CONTENT['breadcrumb']['link']

  const [modalFlags, setModalFlags] = useState({ success: false, cancel: false, upload: false, spinner: false, cleardata: false })
  const [paginationData, setPaginationData] = useState({
    validData: { totalpages: Math.ceil(data.length / 10), curpage: 0, curData: data.slice(0, 10) },
    inValidData: { totalpages: Math.ceil(inValidData.length / 10), curpage: 0, curData: inValidData.slice(0, 10) },
  })
  const [uploadData, setUploadData] = useState({ data: [data.length, data.length, 0, 0], uploadStatus: 'pending', id: 0 })
  const [flag, setFlag] = useState(!errorcount)
  const intervalId = useRef('intial value')
  const abortId = useRef('abort id')
  const list = useRef([`Error data (${inValidData.length})`, `All data (${data.length})`])
  const [currentPage, setCurrentPage] = useState(`Error data (${inValidData.length})`) 

  const abortPolling = (error) => {
    error = error || new Error('Taking too long to upload!')
    clearTimeout(intervalId.current)
    clearTimeout(abortId.current)
    intervalId.current = false
    handleAppError(error)
    setModalFlags((stateflags) => {
      return { ...stateflags, success: false }
    })
  }

  const getUploadStatus = (id) => {
    const { upload_status } = CONTENT
    upload_status(
      { id },
      (res) => {
        //setting invalid.current = false on unmount to avoid infinte looping
        if (intervalId.current) {
          intervalId.current = setTimeout(() => getUploadStatus(id), 1 * 1000) // calling api for every 1 sec
          let { total_records_processed = 0, total_records_in_process = 0, total_records_successful = 0, total_records_failure = 0 } = res.data && res.data.result || {}
          setUploadData({
            data: [total_records_processed || 0, total_records_in_process || 0, total_records_successful || 0, total_records_failure || 0],
            uploadStatus: total_records_successful + total_records_failure === data.length ? 'done' : 'pending',
            id: id,
          })

          if (total_records_successful + total_records_failure === data.length) {
            clearTimeout(intervalId.current)
            clearTimeout(abortId.current)
          }
        }
      },
      (err) => {
        abortPolling(err)
      }
    )
  }

  const onSuccess = (res) => {
    setModalFlags((stateflags) => {
      return { ...stateflags, spinner: false, success: true }
    })
    const id = res.data[CONTENT['id_name']]
    intervalId.current = setTimeout(() => getUploadStatus(id), 0)
    abortId.current =  setTimeout(abortPolling, 1000 * 60 * 7)
  }

  const onError = (res) => {
    setModalFlags((stateflags) => {
      return { ...stateflags, spinner: false }
    })
    handleAppError(res)
  }

  const upload = (type) => {
    let flags = { upload: false }
    if (type === 'yes') {
      const request = CONTENT['request']
      let blob = ''
      let fileName = file.name 
      if([PRE_EMP_BULK_CHECK, PPMC_BULK_CHECK].includes(CONTENT['title'])){
        blob = new Blob([file], { type: 'multipart/form-data' })
      } else {
        blob = new Blob([csvData], { type: 'multipart/form-data' })
        fileName = fileName.split('.')
        fileName = fileName.length > 1 ? `${fileName[0]}.csv` : `customer_data_${moment().valueOf()}.csv`
      }
      let data = new FormData()
      let stepathon_id = sessionStorage.getItem('stepathon_id')
      data.append('file', blob, fileName)
      data.append('validation_passed', validDataCount)
      data.append('email', sessionStorage.getItem('hr_email'))
      data.append('company_id', sessionStorage.getItem('company_id'))
      if(CONTENT['type']){
        data.append('type', CONTENT['type'])
      }
      if(stepathon_id) data.append('id', stepathon_id)
      const options = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: data,
      }

      request(options, onSuccess, onError)
      flags['spinner'] = true
    }

    setModalFlags((stateflags) => {
      return { ...stateflags, ...flags }
    })
  }

  const cancel = (type) => {
    if (type === 'yes') {
      dispatch({ type: 'employee_data', tabledata: null })
      history.push(backpath)
      let name = DISABLE_ICON[query]
      dispatch({ type: '', [name]: false })
    } else {
      setModalFlags((flags) => {
        return { ...flags, cancel: false }
      })
    }
  }

  const paginateHandler = (page, type) => {
    const curpage = page
    const curData = (type === 'validData' ? data : inValidData).slice(curpage * 10, curpage * 10 + 10)
    setPaginationData((prevState) => {
      return { ...prevState, [type]: { ...prevState[type], curpage, curData } }
    })
  }

  const navigateTab = (tab) => {
    const check = tab.text === list.current[1]
    const type = check ? 'validData' : 'inValidData'
    setPaginationData((prevState) => {
      return { ...prevState, [type]: { ...prevState[type], curpage: 0, curData: (check ? data : inValidData).slice(0, 10) } }
    })
    setFlag(check)
    setCurrentPage(tab.text)
  }

  useEffect(() => {
    if (!tabledata) history.push(backpath)

    return () => {
      intervalId.current = false
      clearTimeout(abortId.current)
    }
  }, [])

  const downloadBugReport = async () => {
    const bugReportApi = [PRE_EMP_BULK_CHECK, PPMC_BULK_CHECK].includes(CONTENT['title']) ?
    employeeDataApi.download_recent_pre_emp_upload({id: uploadData.id, type: 'erf'}) :
    StepathonApi.stepathon_team_uploader_bug_report({id: uploadData.id, type: 'erf'})
    try{
      let result = await bugReportApi
      let data = result.data.file
      const url = data.url
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      handleAppError(err)
    }
  }

  const allowBugDownload = uploadData.uploadStatus == 'done' && (([PRE_EMP_BULK_CHECK, PPMC_BULK_CHECK].includes(CONTENT['title']) && uploadData?.data[3] > 1) || ((CONTENT['title'] === 'Create new teams' || CONTENT['title'] === 'Re-upload teams') && uploadData?.data[3] > 0) || ((CONTENT['title'] === 'Create new members' || CONTENT['title'] === 'Re-upload members') && uploadData?.data[3] > 0)) ? true : false;
  const error_check = !!errorcount

  return (
    <Spinner visible={modalFlags.spinner}>
      <EmployeeDataHoc 
        title={CONTENT['title']}
        breadcrumb={(CONTENT['title'] === 'Create new teams' || CONTENT['title'] === 'Re-upload teams'
                    || CONTENT['title'] === 'Create new members' || CONTENT['title'] === 'Re-upload members') ?
          [
            { text: 'Engagement', link: '/stepathon' },
            { text: 'Stepathon', link: '/stepathon' },
            CONTENT['breadcrumb']
          ] :
          [
            CONTENT['title'] === 'Pre employement bulk checkups' ? 
            { text: 'Pre employment', link: '/pre-employee-checkup'  } : 
            { text: 'Manage Employees', link: '/employee-registration' }, CONTENT['breadcrumb']
          ]
        }
        close={() => history.push(backpath)}
      >
        <div className={'grid gap-2 mt-6 ' + (error_check ? 'grid-cols-3' : 'grid-cols-2')}>
          <CountMetricCard metricValue={data.length} metricDescription={`${query=== 'family_addition' ? 'Family members' : 'Employees'} data uploaded`}  svgsrc={CorrectTick} />
          <CountMetricCard metricValue={data.length - errorcount} metricDescription={`${query=== 'family_addition' ? 'Family members' : 'Employees'} data validation passed`} svgsrc={CorrectTick} />
          {error_check && <CountMetricCard metricValue={errorcount} metricDescription='Employees data validation failed, needs attention' svgsrc={exclametry_red_dark} />}
        </div>
        <UserAlert
          icon={error_check ? 'red' : 'green'}
          para={
            error_check ? (
              'There are some validation error in the data. Please resolve the error or proceed with this file.'
            ) : (
                'Your data has been successfully validated. Please click on upload to proceed.'
            )
          }
          paraClasses={`text-gray-700  ${error_check ? 'font-semibold' : ''}`}
          classes={'mt-6 border p-4 ' + (error_check ? 'border-red-500' : 'border-green-600')}
          imgClasses='h-5 w-5'
        />
        <div className='flex justify-between items-end mt-10'>
          {error_check ? 
            <div
               style={{width : '80%'}}
               className='xl:w-5xl'
             >
              <Tabbar  
              list= {[{text : list.current[0]}, {text : list.current[1]}]}
              classes='mt-5'
              currentPage = {currentPage}
              setContract={navigateTab}
              />
            </div>  
            : 
              <h1 className=' text-gray-500 text-xs m-0 tracking-widest leading-3'>ALL DATA ({data.length})</h1>
          } 
          <div className='inline-block'>
           { error_check && 
               <button
               className={joinButtonClasses(dsButtonSecondary, 'mr-3 h-11 px-6')}
               onClick={() =>
                 setModalFlags((flags) => {
                   return { ...flags, cancel: true }
                 })
               }
             >
               Re-upload
            </button>
           }
            <button
              className={joinButtonClasses(dsButtonPrimary, 'h-11 px-6')}
              disabled={SALES_ROLE === "sales"}
              onClick={() =>
                setModalFlags((flags) => {
                  return { ...flags, upload: true }
                })
              }
            >
              Proceed
            </button>
          </div>
        </div>

        <Table
          headers={headers}
          tableClasses='alternate_gray200_color'
          customHeaders = {!flag ?  ['Error Information', ...CONTENT['tableHeaders']] : CONTENT['tableHeaders']}
          data={flag ? paginationData.validData.curData : paginationData.inValidData.curData}
          tableschema={tableSchema}
          showError = {currentPage === `Error data (${inValidData.length})`}
          headerClasses='text-2xs tracking-widest py-4'
          classes='mt-3 rounded-md'
          ellipseFlag={!['family_addition', 'employee_deletion'].includes(query)}
        />
        {(flag ? data.length : inValidData.length) > 0 && <Pagination
          totalpages={flag ? paginationData.validData.totalpages : paginationData.inValidData.totalpages}
          totalresults={flag ? data.length : inValidData.length}
          curpage={flag ? paginationData.validData.curpage : paginationData.inValidData.curpage}
          handler={paginateHandler}
          args={flag ? ['validData'] : ['inValidData']}
        />}
        <UndoModal
          visible={modalFlags['cancel']}
          height={300}
          width={600}
          title={`${CANCEL_QUERY_STATUS[query]} data not processed`}
          description='Do you want to cancel processing the data? The data is not yet processed.'
          imgsrc={close_light_red}
          action={cancel}
        />
        <UploadModal
          visible={modalFlags['upload']}
          title={error_check ? 'Do you want to continue uploading the file with error ? Please note errors rows will not be uploaded.': 'Your file has passed the validation check and is ready to upload. Do you want to continue uploading the file?'}
          height={205}
          width={624}
          handler={upload}
        />
        <UploadStatusModal 
          visible={modalFlags['success']} 
          metric_liners={CONTENT['upload_status_metrics']} 
          description={`If the data ${UPLOAD_QUERY_STATUS[query]} fails you will receive a failed ${UPLOAD_QUERY_STATUS[query]} email, please verify the data and then upload the correct data.`}
          {...uploadData} 
          close={() => cancel('yes')}
          allow_bug_dwnld={allowBugDownload}
          bugReport={downloadBugReport}
        />
      </EmployeeDataHoc>
    </Spinner>
  )
}

export default ViewErrors
